import React, { useEffect, useRef } from 'react';
import QRCodeStyling from 'qr-code-styling';

const QRPlacerCanvas = ({ 
  qrCodeData, 
  theme, 
  tableNumber, 
  restaurantName,
  onQRReady,
  visualTheme = 'lime' // 'lime' or 'orange'
}) => {
  const posterRef = useRef(null);
  const qrRef = useRef(null);
  const qrInstanceRef = useRef(null);

  // Visual theme configurations
  const visualThemes = {
    lime: {
      backgroundColor: '#c5ff4d',
      logoSrc: '/src/assets/logo.png',
      logoAlt: 'Ordyrr',
      accentColor: '#111827'
    },
    orange: {
      backgroundColor: '#FFA500',
      logoSrc: '/src/assets/restaurant-logo.png',
      logoAlt: 'Restaurant',
      accentColor: '#000000'
    },
    green: {
      backgroundColor: '#00E676',
      logoSrc: '/src/assets/logo green.png',
      logoAlt: 'Ordyrr',
      accentColor: '#2D2D2D'
    }
  };

  const activeVisualTheme = visualThemes[visualTheme];

  useEffect(() => {
    if (!qrCodeData || !qrRef.current) return;

    // Create QR code instance with high quality settings
    const qr = new QRCodeStyling({
      width: 200,
      height: 200,
      type: 'svg',
      data: qrCodeData,
      margin: 8,
      qrOptions: {
        errorCorrectionLevel: 'H' // High error correction for better scannability
      },
      backgroundOptions: {
        color: '#ffffff'
      },
      dotsOptions: {
        color: '#111827',
        type: 'rounded'
      },
      cornersSquareOptions: {
        color: '#111827',
        type: 'extra-rounded'
      },
      cornersDotOptions: {
        color: '#111827',
        type: 'dot'
      },
      imageOptions: {
        hideBackgroundDots: true
      }
    });

    // Clear existing content
    qrRef.current.innerHTML = '';
    qr.append(qrRef.current);
    qrInstanceRef.current = qr;

    // Notify parent that QR is ready
    if (onQRReady) {
      onQRReady(posterRef.current);
    }

    return () => {
      if (qrRef.current) {
        qrRef.current.innerHTML = '';
      }
    };
  }, [qrCodeData, onQRReady]);

  return (
    <div 
      ref={posterRef} 
      className="bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col relative"
      style={{ 
        width: '400px', 
        height: '600px', // A6 size: 2:3 aspect ratio (400 x 600)
        maxWidth: '100%'
      }}
    >
      {/* Top Section - Logo & Main Message */}
      <div 
        className="pt-4 pb-2 px-6 text-center flex-shrink-0 rounded-t-3xl"
        style={{ backgroundColor: activeVisualTheme.backgroundColor }}
      >
        {/* Brand Logo */}
        <div className="flex justify-center mb-3">
          <img
            src={activeVisualTheme.logoSrc}
            alt={activeVisualTheme.logoAlt}
            className="h-12 object-contain"
          />
        </div>

        <h1 
          className="text-[2rem] font-black leading-[0.9] tracking-tight whitespace-pre-line"
          style={{ color: activeVisualTheme.accentColor }}
        >
          {theme.header}
        </h1>

        {/* Playful dots */}
        <div className="flex gap-2 justify-center mt-3">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: activeVisualTheme.accentColor }}></div>
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: activeVisualTheme.accentColor }}></div>
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: activeVisualTheme.accentColor }}></div>
        </div>
      </div>

      {/* QR Code Section */}
      <div 
        className="flex items-center justify-center px-6 py-2 flex-1"
        style={{ backgroundColor: activeVisualTheme.backgroundColor }}
      >
        <div className="bg-white rounded-3xl p-5 shadow-xl w-full max-w-[240px] mx-auto">
          {/* Dark tile with inner light QR for strong contrast */}
          <div className="bg-gray-900 rounded-3xl p-2 overflow-hidden grid place-items-center aspect-square shadow-inner">
            {/* Styled QR Code */}
            <div ref={qrRef} className="qr-canvas w-[200px] h-[200px]" />
          </div>

          {/* Scan Text */}
          <div className="mt-2.5 text-center">
            <p className="text-gray-900 font-black text-lg">SCAN HERE</p>
            <p className="text-gray-600 text-[0.62rem] font-bold mt-0.5 tracking-wider">
              ORDYRR · EAT · REPEAT
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Section - Call to Action */}
      <div 
        className="px-6 py-4 text-center flex-shrink-0 rounded-b-3xl"
        style={{ backgroundColor: activeVisualTheme.accentColor }}
      >
        <h2 
          className="text-[1.5rem] font-black leading-[1.0] tracking-tight whitespace-pre-line"
          style={{ color: activeVisualTheme.backgroundColor }}
        >
          {theme.footer}
        </h2>
        <p 
          className="text-[0.65rem] font-bold mt-1"
          style={{ color: activeVisualTheme.backgroundColor }}
        >
          Tap. Eat. Repeat. | Food in 10 mins ⚡
        </p>
      </div>

      {/* Playful accent circles */}
      <div 
        className="absolute top-20 right-6 w-10 h-10 rounded-full opacity-10"
        style={{ backgroundColor: activeVisualTheme.accentColor }}
      ></div>
      <div 
        className="absolute top-32 left-6 w-7 h-7 rounded-full opacity-15"
        style={{ backgroundColor: activeVisualTheme.accentColor }}
      ></div>
    </div>
  );
};

export default QRPlacerCanvas;
